/**
 * Shared types for the case-detail tab tree.
 *
 * Mirror the FastAPI Pydantic responses. We keep them loose (string for
 * Decimals, ISO strings for dates) so we can pass payloads from PostgREST
 * straight through without runtime parsing.
 */

export type Case = {
  id: string;
  title: string;
  reference_number: string | null;
  workflow_type: 'group_review' | 'deal_feasibility';
  status: string;
  base_year: number | null;
  analysis_date: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type Company = {
  id: string;
  case_id: string;
  short_label: string;
  legal_name: string | null;
  company_id_number: string | null;
  company_type: string;
  industry: string | null;
  is_primary: boolean;
  parent_company_id: string | null;
  founded_year: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type FinancialStatement = {
  id: string;
  company_id: string;
  fiscal_year: number;
  is_audited: boolean;
  statement_date: string | null;

  revenue: string | null;
  gross_profit: string | null;
  operating_profit: string | null;
  net_profit: string | null;
  depreciation: string | null;
  financing_expense: string | null;
  tax_expense: string | null;
  dividend: string | null;

  total_assets: string | null;
  current_assets: string | null;
  cash: string | null;
  receivables: string | null;
  inventory: string | null;
  fixed_assets: string | null;
  intangible_assets: string | null;
  leasehold_improvements: string | null;

  payables: string | null;
  short_term_debt: string | null;
  long_term_debt: string | null;
  current_maturities: string | null;
  shareholder_liabilities: string | null;
  related_party_liabilities: string | null;
  total_equity: string | null;

  working_capital_customer_advances: string | null;
  working_capital_supplier_advances: string | null;
  working_capital_prepaid_expenses: string | null;
  working_capital_deferred_income: string | null;
  working_capital_accrued_expenses: string | null;
  working_capital_accrued_income: string | null;

  notes: string | null;
};

export type Facility = {
  id: string;
  company_id: string;
  instrument_type: string;
  loan_type: string | null;
  financing_source_type: string;
  bank_name: string | null;
  facility_label: string | null;
  amount_current: string | null;
  amount_approved: string | null;
  amount_requested: string | null;
  annual_rate: string | null;
  term_months: number | null;
  current_annual_maturities: string | null;
  notes: string | null;
  created_at: string;
};

export type Collateral = {
  id: string;
  company_id: string;
  collateral_type: string;
  description: string | null;
  status: string;
  value_current: string | null;
  value_approved: string | null;
  value_requested: string | null;
  secures_facility_id: string | null;
  haircut_ratio: string | null;
  notes: string | null;
  created_at: string;
};

export type AnalysisSnapshot = {
  snapshot_id: string;
  case_id: string;
  summary: {
    ebitda: number | null;
    free_cash_flow: number | null;
    tangible_equity: number | null;
    static_dscr: number | null;
    min_dscr: number | null;
    min_dscr_year: number | null;
    years_distressed: number | null;
    normative_years: number | null;
    dynamic_dscr_projections?: Array<{
      year: number;
      cash_flow: number;
      total_debt_service: number;
      principal: number;
      interest: number;
      dscr: number | null;
      status: string;
      surplus: number;
    }>;
  };
  created_at: string;
};

// Format helpers shared by tabs
export const fmtMoney = (raw: string | number | null | undefined): string => {
  if (raw == null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(n);
};

export const fmtRate = (raw: string | number | null | undefined): string => {
  if (raw == null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return `${(n * 100).toFixed(2)}%`;
};

export const fmtRatio = (raw: number | null | undefined): string => {
  if (raw == null) return '—';
  if (!Number.isFinite(raw)) return '—';
  return raw.toFixed(2);
};

export const labelOf = (
  list: { value: string; label: string }[],
  value: string | null,
): string => list.find((o) => o.value === value)?.label ?? value ?? '—';

// ---------------------------------------------------------------------------
// Reference data — shared by CreditPortfolioTab, CreditFacilitiesPage,
// CollateralsPage. Single source of truth so dropdowns and labels stay in
// sync across the case-detail tab and the standalone editor pages.
// ---------------------------------------------------------------------------

export type Option = { value: string; label: string };

export const INSTRUMENT_TYPES: Option[] = [
  { value: 'credit_line', label: 'אשראי מתחדש (חח״ד)' },
  { value: 'short_term_loan', label: 'הלוואה לזמן קצר' },
  { value: 'long_term_loan', label: 'הלוואה לזמן ארוך' },
  { value: 'mortgage', label: 'משכנתא' },
  { value: 'bridge_loan', label: 'הלוואת גישור' },
  { value: 'factoring', label: 'פקטורינג' },
  { value: 'leasing', label: 'ליסינג' },
  { value: 'government_guarantee', label: 'ערבות מדינה' },
  { value: 'off_balance_sheet', label: 'מחוץ למאזן' },
];

export const FINANCING_SOURCES: Option[] = [
  { value: 'commercial_bank', label: 'בנק מסחרי' },
  { value: 'non_bank', label: 'חוץ-בנקאי' },
  { value: 'government_fund', label: 'קרן ממשלתית' },
  { value: 'p2b', label: 'P2B' },
  { value: 'insurance_company', label: 'חברת ביטוח' },
  { value: 'pension_fund', label: 'קרן פנסיה' },
  { value: 'private_investor', label: 'משקיע פרטי' },
];

export const LOAN_TYPES: Option[] = [
  { value: 'SHPITZER', label: 'שפיצר' },
  { value: 'EQUAL_PRINCIPAL', label: 'קרן שווה' },
  { value: 'BULLET', label: 'בולט' },
  { value: 'GRACE_FULL', label: 'גרייס מלא' },
  { value: 'GRACE_INTEREST', label: 'גרייס ריבית' },
  { value: 'ROLLING', label: 'מתחדש' },
  { value: 'BALLOON', label: 'בלון' },
  { value: 'STEP_UP', label: 'מדורג' },
  { value: 'CUSTOM', label: 'מותאם' },
];

export const COLLATERAL_TYPES: Option[] = [
  { value: 'real_estate', label: 'נדל״ן' },
  { value: 'equipment', label: 'ציוד' },
  { value: 'inventory', label: 'מלאי' },
  { value: 'receivables', label: 'חייבים' },
  { value: 'cash_deposit', label: 'פיקדון' },
  { value: 'securities', label: 'ניירות ערך' },
  { value: 'personal_guarantee', label: 'ערבות אישית' },
  { value: 'cross_company_guarantee', label: 'ערבות הדדית' },
  { value: 'government_guarantee', label: 'ערבות מדינה' },
  { value: 'vehicle', label: 'רכב' },
];

export const COLLATERAL_STATUSES: Option[] = [
  { value: 'existing', label: 'קיים' },
  { value: 'approved', label: 'מאושר' },
  { value: 'requested', label: 'מבוקש' },
  { value: 'unknown', label: 'לא ידוע' },
];

export const isLoanInstrument = (t: string): boolean =>
  ['short_term_loan', 'long_term_loan', 'mortgage', 'bridge_loan'].includes(t);
